import { useState, useEffect } from "react";
import SEO from "./seo";
import { APP_DATA } from "./constant_data";
const IntroduceXpageIntro = () => {
  const [activeSection, setActiveSection] = useState("what-is-xpage");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "what-is-xpage",
        "platform-overview",
        "key-features",
        "competitive-pricing",
        "landing-page-design",
        "reliable-support",
        "get-started",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="Introducing xPage – Your Complete E-commerce Solution"
        description="Discover xPage, the all-in-one platform that helps you build, launch, and scale your online store effortlessly with AI-powered landing pages, flexible pricing, and 24/7 support."
        keywords="xPage, ecommerce platform, landing page builder, AI landing page, online store builder, xPage features"
        canonical="https://help.xpage.ai/help/introduce-xpage"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title Group */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Introduce xPAGE
          </h1>
          <p className="text-sm text-systemLabel mt-1">
            Welcome to xPAGE - Your complete e-commerce solution
          </p>
        </div>

        {/* Get Started Card */}
        {/* <div className="bg-gradient-to-r from-deepPurple/5 to-deepPurple/10 p-4 rounded-lg border border-systemBorder mb-6">
                    <h2 className="text-deepPurple text-lg font-semibold">🚀 Get Started in Minutes</h2>
                    <p className="text-systemLabel text-sm mt-1">xPAGE is designed to help you launch your online store quickly and efficiently.</p>
                </div> */}

        {/* What is xPAGE Group */}
        <div id="what-is-xpage" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            What is xPAGE?
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            Welcome to xPage, where launching a high-converting landing page is
            as simple as pasting a product link. Built for speed, scale, and
            success, xPage offers everything you need to build, sell, and grow
            without the need for a developer or designer.
          </p>
        </div>

        {/* Platform Overview Group */}
        <div id="platform-overview" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            Platform Overview
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            Our tool is more than just a landing page builder! It's your
            all-in-one brand launcher. Whether you're a beginner testing your
            first product or a skilled entrepreneur scaling multiple brands,
            xPage gives you everything you need in one seamless platform.
          </p>
        </div>

        {/* Key Features Group */}
        <div id="key-features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            Key Features
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            xPage lets you launch a fully optimized, AI-powered landing page in
            just minutes: no coding, design, or setup hassle. With a single
            product link or by entering a simple prompt, it builds a
            conversion-ready landing page tailored for dropshippers, creators,
            influencers, brand owners, and small businesses looking to grow
            quickly.
          </p>
        </div>

        {/* Features Table */}
        <table className="w-full mb-6">
          <tbody className="border divide-y divide-systemBorder">
            <tr className="bg-gray-400/10 transition-colors">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">Feature</strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Description
              </td>
            </tr>
            <tr className="">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  AI-Powered Landing Page Builder
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Instantly generate mobile-first landing pages from a simple
                product link or prompt, with every section built to convert.
                Customize layouts, content, and visuals using AI-powered tools
                that simplify the entire process.
              </td>
            </tr>
            <tr className="bg-gray-400/10 transition-colors">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  Bundles & Upsells
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Increase your average order value with built-in drag-and-drop
                tools that let you create bundles in seconds and no extra apps
                needed.
              </td>
            </tr>
            <tr className="">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  Checkout Customization
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Accept secure payments through Stripe, PayPal, Klarna, Apple
                Pay, and Cash on Delivery. Easily customize checkout fields
                based on payment method or regional preferences to streamline
                the buyer experience.
              </td>
            </tr>
            <tr className="bg-gray-400/10 transition-colors">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  Multi-Domain & Multi-Currency Support
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Manage multiple domains and landing pages from a single
                dashboard. Sell internationally with native support for multiple
                languages and currencies—no plugins required.
              </td>
            </tr>
            <tr className="">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  Advanced Analytics
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Gain full visibility into your landing page's performance with
                real-time dashboards that track sales, conversion rates, traffic
                sources, and customer behavior.
              </td>
            </tr>
            <tr className="bg-gray-400/10 transition-colors">
              <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                <strong className="text-systemTitle text-sm">
                  Brand Identity
                </strong>
              </td>
              <td className="p-3 align-top text-systemLabel text-sm">
                Generate on-brand logos, color schemes, product images, and
                more, all optimized to convert.
              </td>
            </tr>
          </tbody>
        </table>

        {/* Competitive Pricing Group */}
        <div id="competitive-pricing" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            Competitive Pricing
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            xPage delivers smart, budget-friendly pricing designed to support
            every type of seller. With all-in-one features, no hidden app costs,
            and low transaction fees, you can streamline operations, reduce
            expenses, and focus on growing your business with confidence.
          </p>
        </div>

        {/* Landing Page Design Group */}
        <div id="landing-page-design" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            High-Quality Landing Page Design
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            Your landing page is your brand's first impression! Make it
            unforgettable. With xPage, you'll create stunning,
            mobile-first-optimized landing pages designed to captivate and
            convert from the first click.
          </p>
        </div>

        {/* List Group */}
        <div className="mb-6">
          <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
            <li>Every xPage landing page is built to look professional</li>
            <li>No rigid templates!</li>
            <li>
              Easily test different layouts, headlines, and offers with built-in
              features that make optimization simple
            </li>
            <li>
              From persuasive sections to well-placed call-to-actions, every
              inch is optimized for sales
            </li>
          </ul>
        </div>

        {/* Reliable Support Group */}
        <div id="reliable-support" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold">
            Reliable Support
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            When you're building your brand, support isn't optional, it's
            essential.
          </p>
        </div>

        {/* Support Expectations Card */}
        <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-6">
          <h3 className="text-systemTitle text-md font-semibold mb-2">
            What You Can Expect:
          </h3>
          <ul className="text-systemLabel text-sm space-y-1">
            <li>
              <strong className="text-systemTitle">24/7 Live Support:</strong>{" "}
              Real humans, real solutions, any time of day.
            </li>
            <li>
              <strong className="text-systemTitle">In-App Guidance:</strong>{" "}
              Tooltips, walkthroughs, and tutorials to keep you moving forward.
            </li>
            <li>
              <strong className="text-systemTitle">Help Center:</strong>{" "}
              Comprehensive documentation with how-tos, FAQs.
            </li>
            <li>
              <strong className="text-systemTitle">Community Access:</strong>{" "}
              Join other sellers for tips, strategies, and feedback.
            </li>
          </ul>
        </div>

        {/* Get Started Card */}
        <div
          id="get-started"
          className="bg-systemBackground p-4 rounded-lg border border-systemBorder mb-6 scroll-mt-20"
        >
          <h2 className="text-systemTitle text-lg font-semibold">
            Get Started
          </h2>
          <p className="text-systemLabel text-sm mt-1">
            Launch your brand with xPage and start your{" "}
            {APP_DATA.FREE_TRIAL_DAYS}-day free trial today, no credit card
            required.
          </p>
          <p className="text-systemLabel text-sm mt-1">
            With AI-powered design, flexible pricing, and built-in marketing
            tools, xPage is the fastest and smartest way for new sellers or
            growing brands to scale without limits. Whether you're testing a
            product or building a global trend, xPage gives you everything you
            need to launch, sell, and grow effortlessly.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-systemBackground p-3 rounded-lg border border-systemBorder">
            <h3 className="font-semibold text-systemTitle text-lg">
              Easy Management
            </h3>
            <p className="text-systemLabel text-sm mt-1">
              Intuitive dashboard for all your store operations
            </p>
          </div>
          <div className="bg-systemBackground p-3 rounded-lg border border-systemBorder">
            <h3 className="font-semibold text-systemTitle text-lg">
              24/7 Support
            </h3>
            <p className="text-systemLabel text-sm mt-1">
              Round-the-clock customer support
            </p>
          </div>
          <div className="bg-systemBackground p-3 rounded-lg border border-systemBorder">
            <h3 className="font-semibold text-systemTitle text-lg">
              Mobile Ready
            </h3>
            <p className="text-systemLabel text-sm mt-1">
              Optimized for all devices
            </p>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar - Fixed on right */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#what-is-xpage"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "what-is-xpage"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What is xPAGE?
              </a>
            </li>
            <li>
              <a
                href="#platform-overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "platform-overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Platform Overview
              </a>
            </li>
            <li>
              <a
                href="#key-features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Features
              </a>
            </li>
            <li>
              <a
                href="#competitive-pricing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "competitive-pricing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Competitive Pricing
              </a>
            </li>
            <li>
              <a
                href="#landing-page-design"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "landing-page-design"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Landing Page Design
              </a>
            </li>
            <li>
              <a
                href="#reliable-support"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "reliable-support"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Reliable Support
              </a>
            </li>
            <li>
              <a
                href="#get-started"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "get-started"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Get Started
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IntroduceXpageIntro;
